import { useState } from "react";
import { GroupAggregate } from "./types";
import { ItemTable } from "./ItemTable";
import { num, usd } from "./helpers";

interface Props {
  group: GroupAggregate;
  defaultOpen?: boolean;
  nested?: boolean;
}

export const GroupCard = ({ group, defaultOpen = false, nested = false }: Props) => {
  const [open, setOpen] = useState(defaultOpen);

  const headerBg = nested ? "bg-gray-100" : "bg-gray-50";

  return (
    <div className={`rounded-lg border border-gray-200 ${nested ? "" : "shadow-sm"} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-4 py-3 ${headerBg} hover:bg-gray-100 transition-colors`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <svg
            className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-left min-w-0">
            <div className="font-semibold text-gray-900 truncate">{group.label}</div>
            {group.subLabel && <div className="text-xs text-gray-500 truncate">{group.subLabel}</div>}
          </div>
        </div>
        <div className="flex items-center gap-5 flex-shrink-0">
          <Headline label="Net" value={usd(group.totals.totalNetCents)} accent="text-green-700" />
          <Headline label="Units" value={num(group.totals.totalUnitsSold)} />
          <Headline label="Items" value={num(group.items.length || group.subgroups?.length || 0)} />
        </div>
      </button>

      {open && (
        <div className="bg-white border-t border-gray-200 p-3 space-y-3">
          {group.subgroups?.map((sub) => (
            <GroupCard key={sub.key} group={sub} nested />
          ))}
          {group.items.length > 0 && <ItemTable items={group.items} />}
        </div>
      )}
    </div>
  );
};

const Headline = ({ label, value, accent }: { label: string; value: string; accent?: string }) => (
  <div className="text-right">
    <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className={`text-sm font-semibold tabular-nums ${accent ?? "text-gray-800"}`}>{value}</div>
  </div>
);
